import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Visibility as VisibilityIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import OrgLogo from '../components/organizations/OrgLogo';
import PageTitleIcon from '../components/PageTitleIcon';
import OrganizationLogoManager, {
  type OrganizationLogoManagerHandle,
} from '../components/organizations/OrganizationLogoManager';
import { SetupCoachmark } from '../components/selfSetup/SetupCoachmark';
import { PERM_ORG_WRITE, PERM_UPLOAD_CREATE } from '../auth';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { HELP_GUIDE_MANAGE_ORG, HELP_GUIDE_ORG_LOGO } from '../constants/knowledgeBaseTryIt';
import { helpI18n } from '../i18n/help';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';
import { useOrganizationStore } from '../stores/organization.store';
import { useAuthStore } from '../stores/auth.store';
import { OrganizationSchema, OrganizationType, getOrganizationTypeLabel } from '../types/organization.type';
import {
  mergeLogoFieldsIntoPayload,
  resolveLogoFieldsForSubmit,
  type LogoChange,
} from '../utils/organizationLogo.helpers';

const RECYCLER_INDUSTRY_OPTIONS = [
  'Paper', 'Plastic', 'Metal', 'E-waste', 'Glass', 'Organic', 'Other'
];

const Organizations: React.FC = () => {
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isPlatformAdmin, canAccessPermission } = useAuthStore();
  const { isReadOnly } = useAuthPermissions();
  const [helpManageGuide, setHelpManageGuide] = useState(false);
  const [helpManagePhase, setHelpManagePhase] = useState<'edit' | 'save'>('edit');
  const [helpLogoGuide, setHelpLogoGuide] = useState(false);
  const [helpLogoPhase, setHelpLogoPhase] = useState<'pick' | 'save'>('pick');
  const logoRef = useRef<OrganizationLogoManagerHandle>(null);
  const [logoChange, setLogoChange] = useState<LogoChange>('unchanged');
  const legacyPerms = user?.data?.permissions;
  const isLegacyAdmin =
    legacyPerms &&
    !Array.isArray(legacyPerms) &&
    Object.keys(legacyPerms).includes('*') &&
    legacyPerms['*']?.includes('admin');
  const isAdmin = user?.username === 'anas' || isPlatformAdmin || !!isLegacyAdmin;
  const {
    organizations,
    selectedOrganization,
    getOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
  } = useOrganizationStore();

  const [open, setOpen] = useState(false);
  const showLogoPickTour = helpLogoGuide && open && helpLogoPhase === 'pick';
  const showLogoSaveTour = helpLogoGuide && open && helpLogoPhase === 'save';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationSchema | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    organization_type: OrganizationType.RECYCLER,
    google_maps_url: '',
    data: {
      email: '',
      phone_number: '',
      industry: [] as string[],
      more_info: '',
    },
  });

  const industryOptions = {
    [OrganizationType.RECYCLER]: RECYCLER_INDUSTRY_OPTIONS,
    [OrganizationType.TRUCK]: [
      'General Hauling', 'Recycling Transport', 'Hazardous Waste', 'Bulk Waste', 'Other'
    ],
    [OrganizationType.WASTE_OUTLET]: [
      'Landfill', 'Compost', 'Incinerator', 'Transfer Station', 'Other'
    ],
    [OrganizationType.CHARITY]: [
      'Food Bank', 'Clothing Donation', 'General Charity', 'Other'
    ],
  };

  const editOrgId = selectedOrg?.id;
  const canEditLogo = useMemo(() => {
    if (isReadOnly) return false;
    if (!selectedOrg) return isAdmin;
    return (
      canAccessPermission(PERM_ORG_WRITE(editOrgId!), undefined, editOrgId) &&
      canAccessPermission(PERM_UPLOAD_CREATE(editOrgId!), undefined, editOrgId)
    );
  }, [isReadOnly, selectedOrg, isAdmin, canAccessPermission, editOrgId]);

  useEffect(() => {
    getOrganizations();
  }, []);

  const paginatedOrganizations = useMemo(
    () =>
      organizations.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [organizations, page, rowsPerPage],
  );

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const handleOpen = useCallback((org?: OrganizationSchema) => {
    if (org) {
      setSelectedOrg(org);
      setLogoUrl(org.logo_url ?? null);
      setLogoChange('unchanged');
      logoRef.current?.resetLogoChange();
      setFormData({
        name: org.name,
        organization_type: org.organization_type,
        google_maps_url: org.google_maps_url,
        data: {
          email: org.data?.email || '',
          phone_number: org.data?.phone_number || '',
          industry: org.data?.industry || [],
          more_info: org.data?.more_info || '',
        },
      });
    } else {
      setSelectedOrg(null);
      setLogoUrl(null);
      setLogoChange('unchanged');
      logoRef.current?.resetLogoChange();
      setFormData({
        name: '',
        organization_type: OrganizationType.RECYCLER,
        google_maps_url: '',
        data: {
          email: '',
          phone_number: '',
          industry: [],
          more_info: '',
        },
      });
    }
    setOpen(true);
  }, []);

  useEffect(() => {
    const state = location.state as { editOrgId?: string } | null;
    if (!state?.editOrgId || organizations.length === 0) return;
    const org = organizations.find((o) => o.id === state.editOrgId);
    if (!org) return;
    handleOpen(org);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, organizations, handleOpen, navigate, location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/organizations') return;
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (guide === HELP_GUIDE_MANAGE_ORG) {
      markHelpGuideActive(guide);
      setHelpManageGuide(true);
      setHelpManagePhase('edit');
      navigate({ pathname: '/organizations', search: '' }, { replace: true });
      return;
    }
    if (guide === HELP_GUIDE_ORG_LOGO) {
      markHelpGuideActive(guide);
      setHelpLogoGuide(true);
      setHelpLogoPhase('pick');
      navigate({ pathname: '/organizations', search: '' }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!helpManageGuide || helpManagePhase !== 'edit') return;
    window.requestAnimationFrame(() => {
      document
        .getElementById('help-org-edit-button')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [helpManageGuide, helpManagePhase, organizations.length]);

  useEffect(() => {
    if (!showLogoSaveTour) return;
    window.requestAnimationFrame(() => {
      document
        .getElementById('help-org-logo-save')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [showLogoSaveTour]);

  const finishManageGuide = () => {
    setHelpManageGuide(false);
    setHelpManagePhase('edit');
    clearHelpGuideActive();
  };

  const helpTargetOrgId =
    selectedOrganization?.id ?? organizations[0]?.id ?? null;

  // Keep org edit dialog open while the logo coachmark is active
  const skipLogoTourReopenRef = useRef(false);
  useEffect(() => {
    if (!helpLogoGuide || open || organizations.length === 0) return;
    if (skipLogoTourReopenRef.current) {
      skipLogoTourReopenRef.current = false;
      return;
    }
    const orgId = selectedOrganization?.id;
    const org =
      (orgId && organizations.find((o) => o.id === orgId)) ||
      organizations[0];
    if (org) handleOpen(org);
  }, [
    helpLogoGuide,
    open,
    organizations,
    selectedOrganization?.id,
    handleOpen,
  ]);

  const closeDialog = () => {
    setOpen(false);
    setSelectedOrg(null);
    setLogoUrl(null);
    setLogoChange('unchanged');
    logoRef.current?.resetLogoChange();
    setFormData({
      name: '',
      organization_type: OrganizationType.RECYCLER,
      google_maps_url: '',
      data: {
        email: '',
        phone_number: '',
        industry: [],
        more_info: '',
      },
    });
  };

  /** Soft-finish Knowledge Base logo guide. */
  const finishLogoTour = () => {
    if (helpLogoGuide) {
      skipLogoTourReopenRef.current = true;
      setHelpLogoGuide(false);
      setHelpLogoPhase('pick');
      clearHelpGuideActive();
      closeDialog();
      return;
    }
    closeDialog();
  };

  const advanceLogoTourToSave = () => {
    setHelpLogoPhase('save');
  };

  const handleClose = () => {
    if (helpLogoGuide) {
      finishLogoTour();
      return;
    }
    closeDialog();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const pendingLogo = logoRef.current?.getLogoChange() ?? 'unchanged';
      const logoFields = await resolveLogoFieldsForSubmit(
        pendingLogo,
        selectedOrg?.id
      );

      let saved: unknown = null;
      if (selectedOrg) {
        const payload = mergeLogoFieldsIntoPayload(formData, logoFields);
        saved = await updateOrganization(selectedOrg.id, payload);
      } else {
        const payload = mergeLogoFieldsIntoPayload(formData, logoFields);
        saved = await createOrganization(payload);
      }

      if (!saved) {
        return;
      }

      if (helpLogoGuide) {
        skipLogoTourReopenRef.current = true;
        setHelpLogoGuide(false);
        clearHelpGuideActive();
      }
      if (helpManageGuide) {
        finishManageGuide();
      }
      closeDialog();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('organizations.confirmDelete'))) {
      await deleteOrganization(id);
    }
  };

  const getOrganizationTypeColor = (type: OrganizationType) => {
    switch (type) {
      case OrganizationType.RECYCLER:
        return theme.palette.success.main;
      case OrganizationType.WASTE_OUTLET:
        return theme.palette.primary.main;
      case OrganizationType.TRUCK:
        return theme.palette.warning.main;
      case OrganizationType.CHARITY:
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          p: 3,
          borderRadius: "16px",
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PageTitleIcon icon={<BusinessIcon />} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: "text.primary" }}>
            {t('organizations.title')}
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1.5,
            }}
          >
            {t('organizations.add')}
          </Button>
        )}
      </Paper>

      <Paper
        elevation={2}
        sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          bgcolor: theme.palette.background.paper,
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('organizations.columns.name')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('organizations.columns.type')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('organizations.columns.contact')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('organizations.columns.location')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">{t('organizations.columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrganizations.map((org) => (
                <TableRow 
                  key={org.id}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: theme.palette.action.hover,
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <OrgLogo
                        name={org.name}
                        logoUrl={org.logo_url ?? null}
                        size={32}
                      />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {org.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getOrganizationTypeLabel(org.organization_type)}
                      sx={{ 
                        bgcolor: getOrganizationTypeColor(org.organization_type),
                        color: theme.palette.getContrastText(
                          getOrganizationTypeColor(org.organization_type)
                        ),
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {org.data?.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {org.google_maps_url ? (
                      <Link
                        href={org.google_maps_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                          fontSize: '0.875rem',
                        }}
                      >
                        <LocationIcon fontSize="small" />
                        {t('organizations.viewOnMap')}
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => navigate(`/organizations/${org.id}`)}
                      sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          bgcolor: theme.palette.action.hover,
                        },
                      }}
                      aria-label={t('organizations.viewOrganization')}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    {helpManageGuide &&
                    helpManagePhase === 'edit' &&
                    org.id === helpTargetOrgId ? (
                      <SetupCoachmark
                        open
                        title={tHelp('knowledgeBase.manageOrgEditTitle')}
                        subtitle={tHelp('knowledgeBase.manageOrgEditSubtitle')}
                        onDismiss={finishManageGuide}
                      >
                        <IconButton
                          id="help-org-edit-button"
                          onClick={() => {
                            handleOpen(org);
                            setHelpManagePhase('save');
                          }}
                          sx={{
                            color: theme.palette.primary.main,
                            '&:hover': {
                              bgcolor: theme.palette.primary.light,
                              color: theme.palette.primary.contrastText,
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </SetupCoachmark>
                    ) : (
                      <IconButton
                        onClick={() => handleOpen(org)}
                        sx={{
                          color: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: theme.palette.primary.light,
                            color: theme.palette.primary.contrastText,
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {isAdmin && (
                      <IconButton 
                        onClick={() => handleDelete(org.id)}
                        sx={{ 
                          color: theme.palette.error.main,
                          '&:hover': { 
                            bgcolor: theme.palette.error.light,
                            color: theme.palette.error.contrastText,
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={organizations.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common.rangeOf', { from, to, count })}
        />
      </Paper>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            borderRadius: "16px",
            ...(helpLogoGuide
              ? {
                  overflow: "visible",
                }
              : {}),
          }
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: "16px 16px 0 0",
            ...(helpLogoGuide && {
              position: "relative",
              zIndex: 0,
            }),
          }}>
            {selectedOrg ? t('organizations.edit') : t('organizations.add')}
          </DialogTitle>
          <DialogContent
            sx={{
              p: 3,
              ...(helpLogoGuide && {
                overflow: "visible",
              }),
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isAdmin ? (
                <>
                  <TextField
                    label={t('organizations.form.name')}
                    fullWidth
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>{t('organizations.form.type')}</InputLabel>
                    <Select
                      value={formData.organization_type}
                      onChange={(e) => setFormData({ ...formData, organization_type: e.target.value as OrganizationType })}
                      required
                      label={t('organizations.form.type')}
                      sx={{
                        borderRadius: '8px',
                      }}
                    >
                      <MenuItem value={OrganizationType.RECYCLER}>{t('organizations.form.recycler')}</MenuItem>
                      <MenuItem value={OrganizationType.WASTE_OUTLET}>{t('organizations.form.wasteOutlet')}</MenuItem>
                      <MenuItem value={OrganizationType.CHARITY}>{t('organizations.form.charity')}</MenuItem>
                      <MenuItem value={OrganizationType.TRUCK}>{getOrganizationTypeLabel(OrganizationType.TRUCK)}</MenuItem>
                    </Select>
                  </FormControl>
                </>
              ) : (
                <>
                  <TextField
                    label={t('organizations.form.name')}
                    fullWidth
                    value={formData.name}
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                    }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>{t('organizations.form.type')}</InputLabel>
                    <Select
                      value={formData.organization_type}
                      disabled
                      label={t('organizations.form.type')}
                      sx={{
                        borderRadius: '8px',
                      }}
                    >
                      <MenuItem value={formData.organization_type}>
                        {getOrganizationTypeLabel(formData.organization_type)}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}
              <SetupCoachmark
                open={showLogoPickTour}
                title={tHelp('knowledgeBase.orgLogoGuideTitle')}
                subtitle={tHelp('knowledgeBase.orgLogoGuideSubtitle')}
                onDismiss={finishLogoTour}
                onNext={advanceLogoTourToSave}
                nextLabel={t('selfSetup.tourNext')}
                fullWidth
              >
                <Box sx={{ width: '100%' }}>
                  <OrganizationLogoManager
                    ref={logoRef}
                    logoUrl={logoUrl}
                    organizationId={selectedOrg?.id}
                    disabled={!canEditLogo}
                    onLogoChange={(change) => {
                      setLogoChange(change);
                      if (helpLogoGuide && change !== 'unchanged') {
                        setHelpLogoPhase('save');
                      }
                    }}
                  />
                </Box>
              </SetupCoachmark>
              <TextField
                label={t('organizations.form.contactEmail')}
                fullWidth
                value={formData.data.email}
                onChange={(e) => setFormData({ ...formData, data: { ...formData.data, email: e.target.value } })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
              <TextField
                label={t('organizations.form.phoneNumber')}
                fullWidth
                value={formData.data.phone_number}
                onChange={(e) => setFormData({ ...formData, data: { ...formData.data, phone_number: e.target.value } })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
              <FormControl fullWidth>
                <InputLabel>{t('organizations.form.industry')}</InputLabel>
                <Select
                  multiple
                  value={formData.data.industry}
                  onChange={(e) => setFormData({ ...formData, data: { ...formData.data, industry: e.target.value as string[] } })}
                  required
                  label={t('organizations.form.industry')}
                  sx={{
                    borderRadius: '8px',
                  }}
                >
                  {(industryOptions[formData.organization_type] || []).map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{t('organizations.form.industryHelper')}</FormHelperText>
              </FormControl>
              <TextField
                label={t('organizations.form.googleMapsUrl')}
                fullWidth
                value={formData.google_maps_url}
                onChange={(e) => setFormData({ ...formData, google_maps_url: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
              <TextField
                label={t('organizations.form.moreInfo')}
                fullWidth
                multiline
                rows={3}
                value={formData.data.more_info}
                onChange={(e) => setFormData({ ...formData, data: { ...formData.data, more_info: e.target.value } })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={handleClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                },
              }}
            >
              {t('common.cancel')}
            </Button>
            <SetupCoachmark
              open={
                (helpManageGuide && helpManagePhase === 'save' && open) ||
                showLogoSaveTour
              }
              title={
                showLogoSaveTour
                  ? t('selfSetup.tour.logoSaveTitle')
                  : tHelp('knowledgeBase.manageOrgSaveTitle')
              }
              subtitle={
                showLogoSaveTour
                  ? t('selfSetup.tour.logoSaveSubtitle')
                  : tHelp('knowledgeBase.manageOrgSaveSubtitle')
              }
              onDismiss={() => {
                if (showLogoSaveTour) finishLogoTour();
                else if (helpManageGuide) finishManageGuide();
              }}
              onNext={
                showLogoSaveTour
                  ? finishLogoTour
                  : helpManageGuide
                    ? finishManageGuide
                    : undefined
              }
              nextLabel={t('selfSetup.tourNext')}
            >
              <Button 
                id="help-org-logo-save"
                type="submit" 
                variant="contained"
                disabled={isSubmitting}
                sx={{ px: 3, textTransform: 'none', fontWeight: 600 }}
              >
                {selectedOrg ? t('common.update') : t('common.create')}
              </Button>
            </SetupCoachmark>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Organizations; 